import type {
  MediaController,
  MediaControllerEventHandlers,
  MediaControllerInitializeOptions,
  MediaRecorderEventHandlers,
  MediaRecorderWaveformSamples,
  MediaTranscriberEventHandlers,
} from "@/store/audio/recording/audio-recording.types";
import { DesktopMediaRecorder } from "@/store/audio/recording/media-recorder/desktop-media-recorder";
import { DesktopMediaTranscriber } from "@/store/audio/recording/media-transcriber/desktop-media-transcriber";
import { DesktopCaptureService } from "@/modules/audio-recording/capture";
import { DESKTOP_PCM_SAMPLE_RATE } from "@/modules/audio-recording/capture";
import type { AudioLevelProbe } from "@/modules/audio-recording/audio-level-probe";
import { DesktopMediaVisualizer } from "@/store/audio/recording/media-visualizer/desktop-media-visualizer";
import { DesktopPcmProbe } from "@/modules/audio-recording/visualizers";
import { TranscriptionStatus } from "@/store/audio/recording/media-types";
import { TranscriptionMonitor } from "@/store/audio/recording/media-transcriber/transcription-monitor";
import { PreparedRecording } from "@/store/audio/recording/prepared-recording-controller";
import { logger } from "@/modules/logger";
import type { DesktopWaveformUpdate } from "@/modules/audio-recording/desktop-waveform-update";
import { DesktopAudioRecordingStrategy } from "@/modules/electron/types";
import {
  type DesktopControllerKind,
  DesktopRecordingRestartRequiredError,
  normalizeDesktopControllerError,
} from "@/store/audio/recording/media-controller/desktop-controller-errors";
import { createDesktopRecordingInitializationTimeoutError } from "@/store/audio/recording/media-controller/desktop-start-timeout-diagnostics";
import {
  AudioRecordingError,
  AudioRecordingErrorSource,
  AudioRecordingErrorStage,
} from "@/domains/errors";

export type DesktopMediaControllerOptions = {
  eventHandlers: MediaControllerEventHandlers;
  preparedRecording?: PreparedRecording | null;
  recordingStrategy: DesktopAudioRecordingStrategy.WebArtifact;
};

const DESKTOP_MEDIA_CONTROLLER_KIND: DesktopControllerKind = "desktop_media_controller";

export class DesktopMediaController implements MediaController {
  readonly kind = DESKTOP_MEDIA_CONTROLLER_KIND;
  private readonly handlers: MediaControllerEventHandlers;
  private readonly captureService: DesktopCaptureService;
  private readonly recorder: DesktopMediaRecorder;
  private readonly transcriber: DesktopMediaTranscriber;
  private readonly visualizerEngine = new DesktopMediaVisualizer();
  private visualizer: AudioLevelProbe | null = null;
  private probe: DesktopPcmProbe | null = null;
  private isInitialized = false;
  private readonly abortController = new AbortController();
  private recorderNotifiedStop = false;
  private readonly transcriptionMonitor = new TranscriptionMonitor({ maxReconnectAttempts: 5 });
  private readonly recordingStrategy: DesktopAudioRecordingStrategy.WebArtifact;

  constructor({
    eventHandlers,
    preparedRecording,
    recordingStrategy,
  }: DesktopMediaControllerOptions) {
    this.handlers = eventHandlers;
    this.recordingStrategy = recordingStrategy;

    const recorderRef: { current: DesktopMediaRecorder | null } = { current: null };
    const transcriberRef: { current: DesktopMediaTranscriber | null } = { current: null };

    const emitError = (error: unknown, stage?: AudioRecordingErrorStage) => {
      const wrapped =
        error instanceof AudioRecordingError
          ? error
          : new AudioRecordingError({
              source: AudioRecordingErrorSource.RecallDesktop,
              stage,
              message: error instanceof Error ? error.message : String(error),
              originalError: error instanceof Error ? error : undefined,
            });
      this.handlers.onError?.({ error: wrapped });
    };

    this.captureService = new DesktopCaptureService(preparedRecording ?? null, {
      onTranscriptPartial: ({ channel, partialTurn }) => {
        const current = transcriberRef.current;

        if (current) {
          current.handleTranscriptPartial({ channel, partialTurn });
        }
      },
      onTranscriptFinal: ({ channel, finalTurn }) => {
        const current = transcriberRef.current;

        if (current) {
          current.handleTranscriptFinal({ channel, finalTurn });
        }
      },
      onAudioSamples: ({ samples }) => {
        const current = recorderRef.current;
        if (current) {
          current.handleSamples(samples);
        }
      },
      onWaveformUpdate: ({ update }) => {
        this.pushWaveformUpdate(update);
      },
      onDebugEvent: ({ data, kind }) => {
        this.handlers.onDebugEvent?.({ data, kind });
      },
    });

    const recorder = new DesktopMediaRecorder({
      captureService: this.captureService,
      sampleRate: DESKTOP_PCM_SAMPLE_RATE,
      emitError,
    });
    recorderRef.current = recorder;

    const transcriber = new DesktopMediaTranscriber({ emitError });
    transcriberRef.current = transcriber;

    this.recorder = recorder;
    this.transcriber = transcriber;

    const recorderHandlers: MediaRecorderEventHandlers = {
      onAudioChunk: ({ chunk }) => {
        this.handlers.onAudioChunk?.({ chunk });
      },
      onError: ({ error }) => {
        const wrapped =
          error instanceof AudioRecordingError
            ? error
            : new AudioRecordingError({
                source: AudioRecordingErrorSource.RecallDesktop,
                stage: AudioRecordingErrorStage.RecorderEvent,
                message: error instanceof Error ? error.message : String(error),
                originalError: error instanceof Error ? error : undefined,
              });
        this.handlers.onError?.({ error: wrapped });
      },
      onStop: () => {
        this.recorderNotifiedStop = true;
        this.handlers.onStop?.();
      },
    };
    this.recorder.setEventHandlers(recorderHandlers);

    const transcriberHandlers: MediaTranscriberEventHandlers = {
      onTranscriptPartial: ({ partialTurn }) => {
        this.emitTranscriptionStatus(this.transcriptionMonitor.markConnected());
        this.handlers.onTranscriptPartial?.({ partialTurn });
      },
      onTranscriptFinal: ({ finalTurn }) => {
        this.emitTranscriptionStatus(this.transcriptionMonitor.markConnected());
        this.handlers.onTranscriptFinal?.({ finalTurn });
      },
      onError: ({ error }) => {
        const wrapped =
          error instanceof AudioRecordingError
            ? error
            : new AudioRecordingError({
                source: AudioRecordingErrorSource.RecallDesktop,
                stage: AudioRecordingErrorStage.TranscriberEvent,
                message: error instanceof Error ? error.message : String(error),
                originalError: error instanceof Error ? error : undefined,
              });
        this.handlers.onError?.({ error: wrapped });
        this.emitTranscriptionStatus(this.transcriptionMonitor.markDisconnected());
      },
      onStatusChange: ({ status }) => {
        this.handlers.onTranscriptionStatusChange?.({ status });
      },
    };
    this.transcriber.setEventHandlers(transcriberHandlers);
  }

  private static readonly DEFAULT_INITIALIZATION_TIMEOUT_MS = 8_000;

  async initializeAndStart(options: MediaControllerInitializeOptions): Promise<void> {
    const timeoutMs =
      options.initializationTimeoutMs ?? DesktopMediaController.DEFAULT_INITIALIZATION_TIMEOUT_MS;
    let initializationTimeout: ReturnType<typeof setTimeout> | null = null;

    try {
      let initialized = false;
      await Promise.race([
        this._initializeAndStart(options),
        new Promise<never>((_, reject) => {
          initializationTimeout = setTimeout(() => {
            if (initialized) return;

            this.abortController.abort();
            void createDesktopRecordingInitializationTimeoutError({
              controllerKind: DESKTOP_MEDIA_CONTROLLER_KIND,
              logMessage: "[DesktopMediaController] Initialization timed out",
              options,
              preparationProgress: this.captureService.getPreparationProgress(),
              recordingStrategy: this.recordingStrategy,
              timeoutMs,
            }).then(reject);
          }, timeoutMs);
        }),
      ]);
      initialized = true;
    } catch (error) {
      if (error instanceof DesktopRecordingRestartRequiredError) {
        throw error;
      }

      const normalizedError = normalizeDesktopControllerError({
        controllerKind: DESKTOP_MEDIA_CONTROLLER_KIND,
        error,
        operation: "initialize_and_start",
        stage: AudioRecordingErrorStage.Start,
      });

      try {
        await options.onFallbackToWebMedia?.({ error: normalizedError });
      } catch (fallbackError) {
        logger.warn({
          message: "[DesktopMediaController] Failed to handle fallback to web media",
          info: {
            audioRecordingId: options.audioRecordingId,
            error: fallbackError instanceof Error ? fallbackError.message : String(fallbackError),
          },
        });
      }
      throw normalizedError;
    } finally {
      if (initializationTimeout) {
        clearTimeout(initializationTimeout);
      }
    }
  }

  async _initializeAndStart(options: MediaControllerInitializeOptions): Promise<void> {
    await this.initialize(options);
    options.onBeginRecording?.();
    await this.start();
  }

  async initialize(options: MediaControllerInitializeOptions): Promise<void> {
    if (this.isInitialized) return;

    const noteId = options.noteId;
    const audioRecordingId = options.audioRecordingId;

    try {
      if (!audioRecordingId) {
        throw new Error("Desktop recorder requires an associated audio recording.");
      }

      await this.captureService.prepare({
        associatedAudioRecordingId: audioRecordingId,
        calendarEventId: options.calendarEventId,
        associatedNoteId: noteId,
        meetingEventWindowId: options.meetingEventWindowId,
        recordingWindow: options.recordingWindow,
        recordingStrategy: this.recordingStrategy,
        startAttemptTrace: options.startAttemptTrace,
        replacePreviousAudioRecordingId: options.replacePreviousAudioRecordingId,
        abortSignal: this.abortController.signal,
      });
      if (this.abortController.signal.aborted) return;

      await this.ensureVisualizer(DESKTOP_PCM_SAMPLE_RATE);

      this.isInitialized = true;
    } catch (error) {
      throw normalizeDesktopControllerError({
        controllerKind: DESKTOP_MEDIA_CONTROLLER_KIND,
        error,
        operation: "initialize",
        stage: AudioRecordingErrorStage.Start,
      });
    }
  }

  async initializeForRecoveredSession({
    audioRecordingId,
  }: {
    audioRecordingId: string;
  }): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    await this.captureService.attach({
      associatedAudioRecordingId: audioRecordingId,
      recordingStrategy: this.recordingStrategy,
    });

    this.recorder.attachRecoveredSession();
    await this.ensureVisualizer(DESKTOP_PCM_SAMPLE_RATE);
    this.isInitialized = true;
  }

  async start(): Promise<void> {
    if (this.abortController.signal.aborted) return;
    await this.transcriber.start();
    this.emitTranscriptionStatus(this.transcriptionMonitor.markConnected());

    if (this.abortController.signal.aborted) return;

    try {
      await this.recorder.start(this.abortController.signal);
    } catch (error) {
      throw normalizeDesktopControllerError({
        controllerKind: DESKTOP_MEDIA_CONTROLLER_KIND,
        error,
        operation: "start",
        stage: AudioRecordingErrorStage.CaptureStart,
      });
    }

    if (this.abortController.signal.aborted) return;

    this.handlers.onStart?.();
  }

  async stop(): Promise<void> {
    await this.recorder.stop();
    await this.transcriber.stop();
    if (!this.recorderNotifiedStop) {
      this.handlers.onStop?.();
    }
    this.recorderNotifiedStop = false;
    this.isInitialized = false;
    await this.teardownVisualizer();
  }

  async pause(): Promise<void> {
    await this.recorder.pause();
  }

  async resume(): Promise<void> {
    await this.recorder.resume();
  }

  getVisualizer(): AudioLevelProbe | null {
    return this.visualizer;
  }

  dispose(): void {
    this.recorderNotifiedStop = false;
    this.isInitialized = false;
    void this.transcriber.stop();
    void this.recorder.stop();
    this.recorder.dispose();
    this.transcriber.dispose();
    this.captureService.dispose();
    void this.teardownVisualizer();
  }

  private pushWaveformUpdate(update: DesktopWaveformUpdate): void {
    if (!update.samples.length) return;
    this.probe?.pushWaveformUpdate(update);

    if (this.handlers.onWaveformSamples) {
      const payload: MediaRecorderWaveformSamples = {
        sampleRate: update.sampleRate,
        pcm: update.samples.slice(),
        elapsedDurationMs: update.durationMs,
      };
      this.handlers.onWaveformSamples({ samples: payload });
    }
  }

  private async ensureVisualizer(sampleRate: number): Promise<void> {
    try {
      const { visualizer } = await this.visualizerEngine.start({ sampleRate });
      if (this.abortController.signal.aborted) await this.visualizerEngine.stop();
      this.visualizer = visualizer;
      this.probe = visualizer as DesktopPcmProbe;
    } catch {
      this.visualizer = null;
      this.probe = null;
    }
  }

  private async teardownVisualizer(): Promise<void> {
    this.probe = null;
    this.visualizer = null;
    await this.visualizerEngine.stop();
  }

  private emitTranscriptionStatus(status: TranscriptionStatus | null) {
    if (!status) return;
    this.handlers.onTranscriptionStatusChange?.({ status });
  }
}
